import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // For blob responses, return the full response to preserve headers
    if (response.config.responseType === 'blob') {
      return response;
    }
    // For other responses, return just the data
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verifyToken: () => api.get('/auth/verify'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const targetsAPI = {
  getTargets: () => api.get('/targets'),
  getTarget: (id) => api.get(`/targets/${id}`),
  createTarget: (data) => api.post('/targets', data),
  updateTarget: (id, data) => api.put(`/targets/${id}`, data),
  deleteTarget: (id) => api.delete(`/targets/${id}`),
  getTargetLogs: (id, params = {}) => api.get(`/targets/${id}/logs`, { params }),
  getTargetStats: (id, params = {}) => api.get(`/targets/${id}/stats`, { params }),
  getDashboardStats: (params = {}) => api.get('/targets/dashboard', { params }),
  testWebhook: (webhookUrl) => api.post('/targets/test-webhook', { webhook_url: webhookUrl }),
  getConsecutiveThreshold: (id) => api.get(`/targets/${id}/consecutive-threshold`),
  setConsecutiveThreshold: (id, threshold) => api.put(`/targets/${id}/consecutive-threshold`, { threshold }),
  resetConsecutiveCounter: (id) => api.post(`/targets/${id}/reset-consecutive`),
};

export const emailNotificationsAPI = {
  getAll: () => api.get('/email-notifications'),
  create: (data) => api.post('/email-notifications', data),
  update: (id, data) => api.put(`/email-notifications/${id}`, data),
  delete: (id) => api.delete(`/email-notifications/${id}`),
  test: (id) => api.post(`/email-notifications/${id}/test`),
};

export const emailConfigAPI = {
  get: () => api.get('/email-config'),
  create: (data) => api.post('/email-config', data),
  update: (id, data) => api.put(`/email-config/${id}`, data),
  test: (email) => api.post('/email-config/test', { email }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getCurrent: () => api.get('/users/me'),
};

export default api;


export const agentsAPI = {
    getAll: (params = {}) => api.get("/agents", { params }),
    getById: (id) => api.get(`/agents/${id}`),
    create: (data) => api.post("/agents", data),
    update: (id, data) => api.put(`/agents/${id}`, data),
    delete: (id) => api.delete(`/agents/${id}`),
    regenerateToken: (id) => api.post(`/agents/${id}/regenerate-token`),
    regenerateSecret: (id) => api.post(`/agents/${id}/regenerate-secret`),
    getMonitoredItems: (agentId, params = {}) => api.get(`/agents/${agentId}/monitored`, { params }),
    createMonitoredItem: (agentId, data) => api.post(`/agents/${agentId}/monitored`, data),
    updateMonitoredItem: (agentId, itemId, data) => api.put(`/agents/${agentId}/monitored/${itemId}`, data),
    deleteMonitoredItem: (agentId, itemId) => api.delete(`/agents/${agentId}/monitored/${itemId}`),
    getLogs: (agentId, params = {}) => api.get(`/agents/${agentId}/logs`, { params }),
    getResourceHistory: (agentId, params = {}) => api.get(`/agents/${agentId}/resources/history`, { params }),
    setResourceInterval: (agentId, interval) => api.post(`/agents/${agentId}/resources/interval`, { interval }),
    sendCommand: (agentId, data) => api.post(`/agents/${agentId}/command`, data),

    // Process Explorer
    getProcessList: (agentId, params = {}) => api.get(`/agents/${agentId}/processes`, { params, timeout: 25000 }),

    // PM2
    getPM2List: (agentId) => api.get(`/agents/${agentId}/pm2`, { timeout: 20000 }),
    pm2Action: (agentId, data) => api.post(`/agents/${agentId}/pm2/action`, data, { timeout: 25000 }),

    // Event Viewer
    getEventLogs: (agentId, params = {}) => api.get(`/agents/${agentId}/event-viewer`, { params, timeout: 35000 }),

    // Hardware Health
    getHardwareHealth: (agentId) => api.get(`/agents/${agentId}/hardware`, { timeout: 20000 }),
};

export const influxTargetsAPI = {
    getAll: () => api.get('/influx-targets'),
    getById: (id) => api.get(`/influx-targets/${id}`),
    create: (data) => api.post('/influx-targets', data),
    update: (id, data) => api.put(`/influx-targets/${id}`, data),
    delete: (id) => api.delete(`/influx-targets/${id}`),
    fetch: (id) => api.post(`/influx-targets/${id}/fetch`),
    check: (id) => api.post(`/influx-targets/${id}/check`),
    start: (id) => api.post(`/influx-targets/${id}/start`),
    stop: (id) => api.post(`/influx-targets/${id}/stop`),
    testConnection: () => api.post('/influx-targets/test-connection'),
    getConfig: () => api.get('/influx-targets/config'),
    refreshAll: () => api.post('/influx-targets/refresh-all'),
};

export const influxDataAPI = {
    getData: (targetId, params = {}) => api.get(`/influx-data/${targetId}`, { params }),
    getAnomalies: (targetId, params = {}) => api.get(`/influx-data/${targetId}/anomalies`, { params }),
};

export const settingsAPI = {
    getAll: () => api.get('/settings'),
    get: (key) => api.get(`/settings/${key}`),
    set: (key, value) => api.post('/settings', { key, value }),
    delete: (key) => api.delete(`/settings/${key}`),
    getEmailAlertThreshold: () => api.get('/settings/email-alert-threshold'),
    setEmailAlertThreshold: (threshold) => api.post('/settings/email-alert-threshold', { threshold }),
    getConsecutiveTimeoutThreshold: () => api.get('/settings/consecutive-timeout-threshold'),
    setConsecutiveTimeoutThreshold: (threshold) => api.post('/settings/consecutive-timeout-threshold', { threshold }),
};

export const alertsAPI = {
    getActive: (limit = 50, offset = 0) => api.get(`/alerts/active?limit=${limit}&offset=${offset}`),
    getLogs: (limit = 50, offset = 0, search = '') =>
        api.get(`/alerts/logs?limit=${limit}&offset=${offset}&search=${encodeURIComponent(search || '')}`),
    getAcknowledged: (limit = 50, offset = 0) => api.get(`/alerts/acknowledged?limit=${limit}&offset=${offset}`),
    getAll: (limit = 100, offset = 0) => api.get(`/alerts?limit=${limit}&offset=${offset}`),
    acknowledge: (id) => api.post(`/alerts/${id}/acknowledge`),
    acknowledgeAll: (username) => api.post('/alerts/acknowledge-all', { username }),
    acknowledgeIncident: (type, targetId) => api.post('/alerts/incidents/acknowledge', { type, targetId }),
    getCounts: () => api.get('/alerts/counts'),
};


